import type { CalendarDay } from 'v-calendar/dist/types/src/utils/page.d.ts'
import type { LocaleConfig } from '@brighterly/lib-core-types/options/interfaces'
import type { CalendarTooltip } from '@shared/ui/calendars'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { useCalendarLessons } from './calendar-lessons.hook'
import { usePaidStore } from '../store'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface BookingCalendarReturn {
  mounted: Ref<boolean>
  setMounted: (value: boolean) => void
  cachedMonths: Ref<string[]>
  setCachedMonths: (date: string) => void
  cachedAvailableSlots: Ref<string[]>
  setCachedAvailableSlots: (slots: string[]) => void
  cachedAvailableDays: Ref<string[]>
  setCachedAvailableDays: (days: string[]) => void
  minDate: string
  maxDate: string
  locale: ComputedRef<LocaleConfig>
  attributes: Ref<any>
  getDayTooltip: (day: CalendarDay) => CalendarTooltip | undefined
  disabledDates: Ref<any>
  setDisabledDays: (dates: string[]) => void
}
export const useBlockRescheduleCalendar = (): BookingCalendarReturn => {
  const paidStore = usePaidStore()

  const {
    lessonDots,
    getDayTooltip,
  } = useCalendarLessons()

  const mounted = ref<boolean>(false)
  const setMounted = (value: boolean): void => {
    mounted.value = value
  }

  const cachedMonths = ref<string[]>([])
  const setCachedMonths = (date: string): void => {
    cachedMonths.value = [...new Set([...cachedMonths.value, date])]
  }

  const cachedAvailableDays = ref<string[]>([])
  const setCachedAvailableDays = (dates: string[]): void => {
    cachedAvailableDays.value = [...new Set([...cachedAvailableDays.value, ...dates])]
  }

  const cachedAvailableSlots = ref<string[]>([])
  const setCachedAvailableSlots = (slots: string[]): void => {
    cachedAvailableSlots.value = [...new Set([...cachedAvailableSlots.value, ...slots])]
  }

  const minDate = dayjs().format(DateFormat.DefaultDate)
  const nextDate = dayjs(dayjs().add(12, 'hours')).format(DateFormat.DefaultDate)
  const maxDate = dayjs(dayjs().add(1, 'month')).format(DateFormat.DefaultDate)

  const disabledDates = ref([minDate, nextDate])
  const setDisabledDays = (dates: string[]): void => {
    disabledDates.value = [...disabledDates.value, ...dates]
  }

  const locale = computed<LocaleConfig>(() => {
    return {
      id: 'en',
      firstDayOfWeek: 1,
      masks: {
        weekdays: 'WW', title: 'MMMM, YYYY',
      },
    }
  })

  const attributes = computed(() => [
    {
      key: 'today',
      highlight: 'today-attr',
      dates: dayjs().format(DateFormat.DefaultDate),
    },
    {
      key: 'selected',
      highlight: 'selected',
      dates: paidStore.getRescheduleDate,
    },
    ...lessonDots.value,
  ])

  return {
    mounted,
    setMounted,
    cachedMonths,
    setCachedMonths,
    cachedAvailableSlots,
    setCachedAvailableSlots,
    cachedAvailableDays,
    setCachedAvailableDays,
    minDate,
    maxDate,
    locale,
    attributes,
    getDayTooltip,
    disabledDates,
    setDisabledDays,
  }
}
